import Links from '@/shared/constants/navegacao';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { Link } from 'react-router-dom';
import { readFuncao } from '@/modules/structure/funcao/funcao.service';
import { extrairYyyyMm, formatarYyyyMmMesExtensoOuIndefinido } from '@/shared/utils/datas';
import { formatarMoeda } from '@/shared/utils/numeros';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import { podeAcessarSimuladorFolha } from '@/modules/people/simulador-folha/simulador-folha.access';
import { useEffect, useMemo, useState } from 'react';

interface ReadFuncaoDto {
  id: number;
  nome: string;
  ultimoSalarioVigente?: number;
  ultimoSalarioVigencia?: string;
  editadoPor?: { id: number; nome: string };
  editadoEm?: Date;
}

export default function FuncaoIndexPage() {
  const { setActions } = useBreadcrumbActions();
  const { effectiveView } = useNavigationView();
  const podeSimulador = useMemo(
    () => podeAcessarSimuladorFolha(effectiveView),
    [effectiveView],
  );
  const [registros, setRegistros] = useState<[ReadFuncaoDto[], number]>();
  const tableState = useDataTableState('nome');

  const handleBuscar = () => {
    readFuncao({
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      orderBy: tableState.sortConfig.direction,
      orderField: tableState.sortConfig.key,
    }).then((result) => {
      setRegistros(result);
    }).catch((res) => {
      toast.error(String(res) || '');
    });
  };

  useEffect(() => {
    handleBuscar();
  }, []);

  useEffect(() => {
    setActions({
      primary: { label: 'Cadastrar', href: Links.Funcao_Criar, onClick: () => { } },
      ...(podeSimulador
        ? {
            secondary: {
              label: 'Simulador de Folha',
              href: Links.SimuladorFolha_Index,
              onClick: () => { },
            },
          }
        : {}),
    });
    return () => setActions({});
  }, [setActions, podeSimulador]);

  const colunas: Column<ReadFuncaoDto>[] = [
    { key: 'nome', header: 'Nome' },
    {
      key: 'ultimoSalarioVigente',
      header: 'Salário vigente',
      render: (row) => {
        if (row.ultimoSalarioVigente == null) {
          return <span className="text-muted">—</span>;
        }
        const competencia = row.ultimoSalarioVigencia
          ? formatarYyyyMmMesExtensoOuIndefinido(extrairYyyyMm(row.ultimoSalarioVigencia))
          : null;
        return (
          <span title={competencia ? `Competência: ${competencia}` : undefined}>
            {formatarMoeda(row.ultimoSalarioVigente)}
            {competencia && (
              <small className="text-muted d-block">{competencia}</small>
            )}
          </span>
        );
      },
    },
    {
      key: 'acoes',
      header: 'Ações',
      render: (row) => (
        <Link className="link-secondary" to={Links.Funcao_Editar + '/' + row.id}>
          Editar
        </Link>
      ),
    },
  ];

  return (
    <div className="row">
      <div className="col-12">
        <h3>Funções e Cargos</h3>
        <span className="blockquote-footer pb-4 text-end">
          Funções dos colaboradores.
        </span>
      </div>
      <hr className="pb-4" />

      {registros && (
        <DataTable
          columns={colunas}
          data={registros[0]}
          initialSortKey="nome"
          onChange={handleBuscar}
          serverSide={true}
          states={tableState}
          totalRecords={registros[1]}
        />
      )}
    </div>
  );
}
